/**
 * AppShell — the persistent chrome around every signed-in page.
 *
 * Responsibilities:
 *   * Topbar (brand + theme toggle + user chip)
 *   * Left rail on desktop with icon-labelled nav
 *   * Fixed bottom tab bar on mobile (< 900px) — replaces the rail
 *   * Sign-out button (desktop only; mobile Settings hosts it)
 *   * The "Approve on Mobile" modal state is per-page — the shell
 *     provides an `openApproval` slot that pages can wire into their
 *     money-moving CTAs. Pages that don't need it can pass `null`.
 *
 * Every signed-in route wraps its main content in <AppShell current="X">.
 * The `current` prop drives the active-state highlight in both nav
 * surfaces. The nav destinations are declared once here so adding a
 * page means editing this file only.
 */

import { Link, useLocation } from "react-router-dom";
import type { ReactNode } from "react";
import { Avatar, Button, ThemeToggle } from "../components";
import { useAuth } from "../auth/AuthContext";
import styles from "./AppShell.module.css";

// ---------------------------------------------------------------------------
// Icons. Inline SVG, stroke-currentColor so nav items pick up token
// colours automatically on light/dark theme flips.
// ---------------------------------------------------------------------------

const iconStroke = {
  stroke: "currentColor",
  strokeWidth: 1.7,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  fill: "none",
} as const;

function IconHome() {
  return (
    <svg viewBox="0 0 24 24" width={20} height={20} aria-hidden {...iconStroke}>
      <path d="M3.5 11.5 12 4l8.5 7.5" />
      <path d="M5.5 10v10h13V10" />
      <path d="M10 20v-5h4v5" />
    </svg>
  );
}
function IconActivity() {
  return (
    <svg viewBox="0 0 24 24" width={20} height={20} aria-hidden {...iconStroke}>
      <path d="M3 12h4l3-8 4 16 3-8h4" />
    </svg>
  );
}
function IconStatements() {
  return (
    <svg viewBox="0 0 24 24" width={20} height={20} aria-hidden {...iconStroke}>
      <path d="M6 3h9l4 4v14H6z" />
      <path d="M15 3v4h4" />
      <path d="M9 12h6M9 16h6" />
    </svg>
  );
}
function IconMarkets() {
  return (
    <svg viewBox="0 0 24 24" width={20} height={20} aria-hidden {...iconStroke}>
      <path d="M3 17l5-5 4 4 8-9" />
      <path d="M14 7h6v6" />
    </svg>
  );
}
function IconSettings() {
  return (
    <svg viewBox="0 0 24 24" width={20} height={20} aria-hidden {...iconStroke}>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2v3M12 19v3M4.2 4.2l2.1 2.1M17.7 17.7l2.1 2.1M2 12h3M19 12h3M4.2 19.8l2.1-2.1M17.7 6.3l2.1-2.1" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Route table. Order controls both the rail and the bottom tab bar.
// ---------------------------------------------------------------------------

export type NavKey = "home" | "activity" | "statements" | "markets" | "settings";

interface NavItem {
  key: NavKey;
  label: string;
  path: string;
  Icon: () => JSX.Element;
}

const NAV_ITEMS: NavItem[] = [
  { key: "home", label: "Home", path: "/", Icon: IconHome },
  { key: "activity", label: "Activity", path: "/activity", Icon: IconActivity },
  { key: "statements", label: "Statements", path: "/statements", Icon: IconStatements },
  { key: "markets", label: "Markets", path: "/markets", Icon: IconMarkets },
  { key: "settings", label: "Settings", path: "/settings", Icon: IconSettings },
];

// ---------------------------------------------------------------------------
// AppShell.
// ---------------------------------------------------------------------------

interface AppShellProps {
  current: NavKey;
  children: ReactNode;
}

export function AppShell({ current, children }: AppShellProps) {
  const { me, session, signOut } = useAuthWithMe();
  const _location = useLocation(); // reserved: useful for future breadcrumbs
  void _location;

  return (
    <div className={styles.shell}>
      {/* ---- Topbar ---- */}
      <header className={styles.topbar}>
        <Link to="/" className={styles.brand} aria-label="AsterCash home">
          <img
            src="/logo.png"
            className={styles.brandMark}
            alt=""
            width={28}
            height={28}
          />
          <span className={styles.brandName}>AsterCash</span>
          <span className={styles.brandSub}>· web</span>
        </Link>
        <div className={styles.topbarRight}>
          <ThemeToggle inline />
          <div className={styles.userMenu}>
            <Avatar
              initials={
                me?.handle?.slice(1, 3).toUpperCase() ||
                (session?.user_id || "??").slice(0, 2).toUpperCase()
              }
              size={32}
            />
            <span className={styles.userHandle}>
              {me?.handle || "…loading"}
            </span>
            <span className={styles.userMenuAction}>
              <Button variant="ghost" size="sm" onClick={() => void signOut()}>
                Sign out
              </Button>
            </span>
          </div>
        </div>
      </header>

      <div className={styles.body}>
        {/* ---- Left rail (desktop) ---- */}
        <aside className={styles.rail}>
          <nav className={styles.nav}>
            {NAV_ITEMS.map(({ key, label, path, Icon }) => {
              const active = key === current;
              return (
                <Link
                  key={key}
                  to={path}
                  className={`${styles.navItem} ${active ? styles.navItemActive : ""}`}
                  aria-current={active ? "page" : undefined}
                >
                  <span className={styles.navIcon}>
                    <Icon />
                  </span>
                  {label}
                </Link>
              );
            })}
          </nav>
          <div className={styles.railFoot}>
            <p className={styles.railFootEyebrow}>SESSION</p>
            <p className={styles.railFootLine}>
              {session?.session_kind === "web" ? "Web · read-only" : "Device"}
            </p>
            <p className={styles.railFootHint}>
              Money-moving actions open on your mobile device.
            </p>
          </div>
        </aside>

        {/* ---- Main slot ---- */}
        <main className={styles.main}>{children}</main>
      </div>

      {/* ---- Fixed bottom tab bar (mobile only) ---- */}
      <nav className={styles.bottomNav} aria-label="Primary">
        <div className={styles.bottomNavInner}>
          {NAV_ITEMS.map(({ key, label, path, Icon }) => {
            const active = key === current;
            return (
              <Link
                key={key}
                to={path}
                className={`${styles.bottomNavItem} ${
                  active ? styles.bottomNavItemActive : ""
                }`}
                aria-current={active ? "page" : undefined}
              >
                <span className={styles.bottomNavIcon}>
                  <Icon />
                </span>
                {label}
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Small hook — Home fetches /me on its own; other pages don't. We
// centralise the "load /me once per shell mount" concern here so the
// topbar renders the handle regardless of which page is active.
// ---------------------------------------------------------------------------

import { useEffect, useState } from "react";
import { fetchMe, type MeOut } from "../api/wallet";

function useAuthWithMe() {
  const auth = useAuth();
  const [me, setMe] = useState<MeOut | null>(null);
  useEffect(() => {
    if (auth.status !== "signed_in") return;
    let cancelled = false;
    fetchMe()
      .then((v) => {
        if (!cancelled) setMe(v);
      })
      .catch(() => {
        // /me failure is non-blocking — fall back to the user_id
        // initials, already handled in the topbar avatar.
      });
    return () => {
      cancelled = true;
    };
  }, [auth.status]);
  return { ...auth, me };
}
